import { Controller } from 'stimulus'
import { inheritsFrom } from '~/common/extensions/inherits_from'
import {
  updateQuantity,
  updateCartOnBirthDateChange,
} from '~/site/pages/cart/common'
import './entrants_form_component.scss'

export default class extends Controller {
  static targets = ['entrantForm']
  static values = {
    productId: String,
  }

  connect() {
    this.makeAllFirstEntrantsInheritFromInformationFields()
    this.element.dataset.entrantsFormComponentControllerConnected = true

    // This used to be a target but since we now use this from the cart checkout
    // the select element might live outside this form.
    this.quantitySelect = document.querySelector(
      `#product_${this.productIdValue}_quantity`
    )
  }

  makeAllFirstEntrantsInheritFromInformationFields() {
    // The first entrant fields per product
    const firstEntrantFields = document.querySelectorAll(
      '.js-first-product-entrant'
    )

    if (firstEntrantFields.length < 1) {
      return
    }

    firstEntrantFields.forEach((fieldContainer) => {
      this.makeEntrantInheritFromInformationFields(fieldContainer)
    })
  }

  makeEntrantInheritFromInformationFields(fieldContainer) {
    const inheritableFields = []

    // Inheriting email is simple - always 1:1 mapping
    inheritableFields.push(['[email]', ['purchase[email]']])

    // When entrant has name with 1 field and purchase has name with 1 field
    inheritableFields.push(['[name]', ['purchase[name]']])

    // When entrant has name with 1 field and purchase has name with 2 fields
    inheritableFields.push([
      '[name]',
      ['purchase[first_names]', 'purchase[last_name]'],
    ])

    // When entrant has first name field and purchase has name with 2 fields
    inheritableFields.push(['[first_names]', ['purchase[first_names]']])

    // When entrant has last name field and purchase has name with 2 fields
    inheritableFields.push(['[last_name]', ['purchase[last_name]']])

    // When entrant has first name field and purchase has name with 1 fields
    inheritableFields.push([
      '[first_names]',
      ['purchase[name]'],
      { getSourceVal: ($source) => $source.val().trim().split(' ')[0] }, // Split by space and use the first part
    ])

    // When entrant has last name field and purchase has name with 1 fields
    inheritableFields.push([
      '[last_name]',
      ['purchase[name]'],
      {
        getSourceVal: ($source) =>
          $source.val().trim().split(' ').slice(1).join(' '),
      }, // Split by space and use the part after the first space
    ])

    inheritableFields.forEach(
      ([fieldName, fieldToInheritFromNames, options]) => {
        const field = fieldContainer.querySelector(
          `input[name*='${fieldName}']`
        )
        if (!field) return

        const fieldToInheritFromSelector = fieldToInheritFromNames
          .map((name) => `input[name='${name}']`)
          .filter((selector) => document.querySelector(selector))
          .join(', ')
        if (!fieldToInheritFromSelector) return

        inheritsFrom(fieldToInheritFromSelector, field, options)
      }
    )
  }

  onQuantityChange(e) {
    if (e.target.value) {
      updateQuantity()
    }
  }
  toggleGiftFields(event) {
    const giftFieldsContainer = event.target
      .closest('.entrant-fields')
      .querySelector('.entrant-gift-fields')

    giftFieldsContainer.querySelectorAll('input, textarea').forEach((el) => {
      el.disabled = !el.disabled
    })
    giftFieldsContainer.classList.toggle('hidden')

    if (!giftFieldsContainer.classList.contains('hidden')) {
      giftFieldsContainer.querySelector('input:first-of-type')?.focus()
      giftFieldsContainer.querySelector('input:first-of-type')?.showPicker()
    }
  }

  addNewEntrant(event) {
    event.preventDefault()
    this.changeQuantitySelectValueBy(1)
  }

  removeEntrant(event) {
    event.preventDefault()
    event.target.closest('.js-entrant-fields-container').remove()
    this.changeQuantitySelectValueBy(-1)
  }

  changeQuantitySelectValueBy(by) {
    const val = parseInt(this.quantitySelect.value, 10)
    this.quantitySelect.value = val + by
    this.quantitySelect.dispatchEvent(new Event('change'))
  }

  copyEntrantFields({ params: { entrantNumber } }) {
    // entrantNumber is the clicked entrant's number (numbers start at 1, so not the real index in the array of forms/entrants)
    if (entrantNumber === 1) return // nothing to copy from

    const formToCopy = this.entrantFormTargets[entrantNumber - 2]
    const destinationForm = this.entrantFormTargets[entrantNumber - 1]

    if (this.handleCountryFieldCopy(formToCopy, destinationForm)) {
      return setTimeout(() => {
        this.copyEntrantFields({ params: { entrantNumber } })
      }, 400)
    }

    this.copyFormFields(formToCopy, destinationForm)
  }

  // copy all form fields from one form to another, quering by the given (or default) selectors
  // selectors might change, e.g. we only want to change select tags for country and region
  copyFormFields(
    formToCopy,
    destinationForm,
    selectors = 'input, textarea, select',
    options = {}
  ) {
    const formToCopyFields = formToCopy.querySelectorAll(selectors)
    const destinationFormFields = destinationForm.querySelectorAll(selectors)

    // Trailing "[field_key]" segment of a field name, ignoring a multi-value "[]" suffix
    // (checkboxes) and a leading index prefix. Used to match fields across participants, whose
    // names differ by entrant index (…[1][field_x_value] vs …[2][field_x_value]).
    const normSuffix = (name) =>
      (name.replace(/\[\]$/, '').match(/\[([^\]]+)\]$/)?.[1] ?? '').replace(
        /^\d+_/,
        ''
      )

    formToCopyFields.forEach((field, index) => {
      // do not copy name, last name, or email fields
      if (
        ['first_names', 'last_name', 'email'].some((fieldName) =>
          field.name.includes(fieldName)
        )
      )
        return

      let destinationField
      const addressTarget = field.dataset.addressFieldTarget
      if (addressTarget) {
        // Address sub-fields carry a data-address-field-target attribute that names them
        // semantically (addressField, cityField, etc.). Match on that rather than on the
        // field name, which differs between billing (bare: "purchase[address]") and
        // participant custom fields (prefixed: "purchase[…][field_123_value]").
        destinationField = Array.from(destinationFormFields).find(
          (f) => f.dataset.addressFieldTarget === addressTarget
        )
      } else if (options.forBillingAddress) {
        // Non-address billing fields (e.g. show_company_name hidden) have no counterpart
        // in the participant address section — skip them.
        return
      } else {
        // Participant→participant matching on the trailing field key (normSuffix), which is
        // stable across participants despite their differing entrant index. Checkboxes & radios
        // expose several options under one key, so disambiguate by the option's unique value
        // rather than the positional index — that index silently misaligns when the two forms
        // hold different numbers of inputs (e.g. one rendered a dropdown_select that injected
        // an extra element), toggling the wrong option or none at all.
        const sourceSuffix = normSuffix(field.name)
        if (sourceSuffix) {
          const matches = Array.from(destinationFormFields).filter(
            (f) => normSuffix(f.name) === sourceSuffix
          )
          if (matches.length === 1) destinationField = matches[0]
          else if (matches.length > 1)
            destinationField = matches.find((f) => f.value === field.value)
        }
        if (!destinationField) {
          const formsLengthDifference =
            destinationFormFields.length - formToCopyFields.length
          destinationField =
            destinationFormFields[index + formsLengthDifference]
        }
      }

      if (!destinationField) return

      if (['checkbox', 'radio'].includes(field.type)) {
        // for checkboxes/radio buttons, it is better to click them, and let them handle the change event (e.g. the 'gift' checkbox)
        if (field.checked != destinationField.checked)
          return destinationField.click()
      }

      // We don't want to erease the value from the destination field if the source field is empty
      if (!field.value || field.value == destinationField.value) return

      destinationField.value = field.value
      destinationField.dispatchEvent(new Event('change')) // just in case
    })

    this.handleAddressLineVisibility(formToCopy, destinationForm)
  }

  // if we need to update a country field, we should do it before any other field, and give the form time to update some related fields (e.g. states of the country)
  // this function returns true if it updated the country field, and false otherwise
  handleCountryFieldCopy(formToCopy, destinationForm) {
    const originCountryField = formToCopy.querySelector(
      'select[name*="country_code"]'
    )
    if (originCountryField) {
      const destinationCountryField = destinationForm.querySelector(
        'select[name*="country_code"]'
      )
      if (originCountryField.value !== destinationCountryField.value) {
        destinationCountryField.value = originCountryField.value
        return destinationCountryField.dispatchEvent(new Event('change'))
      }
    }

    return false
  }

  // make second address line visible if it is visible in the form to copy
  handleAddressLineVisibility(formToCopy, destinationForm) {
    const groupAddress = formToCopy.querySelector('.group\\/address_field')
    if (groupAddress && groupAddress.classList.contains('address2-visible')) {
      destinationForm
        .querySelector('.group\\/address_field')
        .classList.add('address2-visible')
    }
  }

  // copy billing address into the address of first participant
  // we could have a 'special' case where billing form only ask for country and state, and the destination form has more fields
  copyBillingAddress() {
    const billingSection = document.querySelector('#cart-address-info')

    // when the billing address only has country/state, it only has select tags and 1 hidden input
    const hasBillingAddress =
      billingSection.querySelectorAll('input').length > 1

    const selectors = hasBillingAddress ? 'input, select' : 'select'

    const destinationAddressSection = this.entrantFormTargets[0].querySelector(
      '.group\\/address_field'
    ).parentElement

    if (
      this.handleCountryFieldCopy(billingSection, destinationAddressSection)
    ) {
      return setTimeout(() => {
        this.copyBillingAddress()
      }, 400)
    }

    this.copyFormFields(billingSection, destinationAddressSection, selectors, {
      forBillingAddress: true,
    })
  }

  birthDateChanged(event) {
    const birthDateInput = event.target
    const birthDateValue = birthDateInput.value

    // Only trigger cart update if we have a complete date
    if (this.isValidCompleteDate(birthDateValue)) {
      updateCartOnBirthDateChange()
    }
  }

  // Check if the date string represents a complete, valid date
  // The format is forced by the datepicker, being something like yyyy-mm-dd
  isValidCompleteDate(dateString) {
    if (!dateString) return false

    const date = new Date(dateString)
    return date instanceof Date && !isNaN(date) && dateString.includes('-')
  }
}
